/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
    Page, PageHeader, LinkButton, Button, StatTile, SectionHeader, EmptyState, Skeleton, StatusTag, KV, formatINR,
} from '../components/console/ui';
import { GridIc, DbIc, ServersIc, DriveIc, FolderIc, UsersIc } from '../components/console/icons';
import { useSelectedProject } from '../hooks/useSelectedProject';
import { projectService } from '../services/api';
import { useBuckets } from '../hooks/useBuckets';
import { useContainers } from '../hooks/useContainers';
import { useVolumes } from '../hooks/useVolumes';
import { useVMs } from '../hooks/useVMs';
import { isVMManagerEnabled, isFileStorageEnabled } from '../config/featureFlags';

const ProjectOverview: React.FC = () => {
    const { id: projectId } = useParams<{ id: string }>();
    const { selectedProjectId, setSelectedProject } = useSelectedProject();

    // Fetch project details
    const { data: projectData, isLoading: projectLoading } = useQuery({
        queryKey: ['project', projectId],
        queryFn: () => projectService.get(projectId!).then((res) => res.data),
        enabled: !!projectId,
    });

    // Use modular hooks for resources
    const { containers, runningContainers, totalCount: podsCount } = useContainers(projectId);
    const { buckets, totalCount: bucketsCount } = useBuckets(projectId || '');
    const { volumes, totalCount: volumesCount } = useVolumes(projectId || '');
    // Always call the hook (React Hooks rule), but conditionally enable the query
    const { runningCount: runningVMsCount, totalCount: vmsCount } = useVMs({
      projectId: projectId || '',
      enabled: isVMManagerEnabled()
    });

    // Handle project response format
    const project = projectData?.project || projectData;

    // Calculate project-specific spending from resources
    const projectSpending = useMemo(() => {
        let containerSpending = 0;
        let bucketSpending = 0;
        let volumeSpending = 0;

        // Sum container spending
        containers.forEach((container: any) => {
            containerSpending += container.total_billed_inr || 0;
        });

        // Sum bucket spending
        buckets.forEach((bucket: any) => {
            bucketSpending += bucket.total_billed_inr || 0;
        });

        // Sum volume spending (if available)
        volumes.forEach((volume: any) => {
            volumeSpending += volume.total_billed_inr || 0;
        });

        const total = containerSpending + bucketSpending + volumeSpending;

        return {
            total,
            containers: containerSpending,
            buckets: bucketSpending,
            volumes: volumeSpending,
        };
    }, [containers, buckets, volumes]);

    const resourceStats = [
        { name: 'Pods', count: podsCount, sub: `${runningContainers.length} running`, icon: <GridIc />, href: `/pods?project=${projectId}` },
        ...(isFileStorageEnabled()
            ? [{ name: 'Buckets', count: bucketsCount, sub: 'Object storage', icon: <DbIc />, href: `/storage?project=${projectId}` }]
            : []),
        ...(isVMManagerEnabled()
            ? [{ name: 'VMs', count: vmsCount, sub: `${runningVMsCount} running`, icon: <ServersIc />, href: `/vms?project=${projectId}` }]
            : []),
        { name: 'NFS volumes', count: volumesCount, sub: 'Persistent storage', icon: <DriveIc />, href: `/volumes?project=${projectId}` },
    ];

    if (projectLoading) {
        return (
            <Page>
                <Skeleton className="h-9 w-64" />
                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                    {[1, 2, 3, 4].map((i) => (
                        <Skeleton key={i} className="h-28 rounded-[14px]" />
                    ))}
                </div>
                <Skeleton className="h-64 rounded-[14px]" />
            </Page>
        );
    }

    if (!project) {
        return (
            <Page>
                <EmptyState
                    icon={<FolderIc />}
                    title="Project not found"
                    body="The project you're looking for doesn't exist or you no longer have access to it."
                    actions={<LinkButton to="/projects">Back to projects</LinkButton>}
                />
            </Page>
        );
    }

    const isCurrent = selectedProjectId === projectId;

    return (
        <Page>
            <PageHeader
                back={{ to: '/projects', label: 'Projects' }}
                title={project.name}
                subtitle={project.description || 'No description'}
                actions={
                    <>
                        {!isCurrent && projectId && (
                            <Button variant="secondary" onClick={() => setSelectedProject(projectId)}>
                                Switch to project
                            </Button>
                        )}
                        <LinkButton to={`/projects/${projectId}/settings`} variant="secondary">
                            <UsersIc className="w-4 h-4" />
                            Team
                        </LinkButton>
                    </>
                }
            />

            <div className="flex flex-wrap gap-x-6 gap-y-1 text-[13px] text-ps-subtle">
                <span>Created {format(new Date(project.created_at), 'd MMM yyyy')}</span>
                {project.id && <span className="font-mono">ID {project.id}</span>}
                {isCurrent && <span className="text-ps-accent">Current project</span>}
            </div>

            <section className="flex flex-col gap-3" aria-label="Resources">
                <SectionHeader title="Resources" />
                <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(min(100%,220px),1fr))]">
                    {resourceStats.map((stat) => (
                        <StatTile key={stat.name} label={stat.name} value={stat.count} sub={stat.sub} icon={stat.icon} to={stat.href} />
                    ))}
                </div>
            </section>

            <section className="ps-card p-5 flex flex-col gap-4" aria-labelledby="proj-spend">
                <div className="flex flex-wrap justify-between items-end gap-3">
                    <div>
                        <h2 id="proj-spend" className="m-0 text-lg font-medium text-ps-text">Billed to this project</h2>
                        <p className="m-0 mt-1 text-sm text-ps-muted">Total billed for the project’s current resources</p>
                    </div>
                    <div className="text-2xl font-semibold text-ps-text">{formatINR(projectSpending.total)}</div>
                </div>
                <div className="flex flex-col gap-2 max-w-md">
                    <KV k={`Pods (${podsCount})`} v={formatINR(projectSpending.containers)} />
                    {isFileStorageEnabled() && <KV k={`Object storage (${bucketsCount})`} v={formatINR(projectSpending.buckets)} />}
                    <KV k={`NFS volumes (${volumesCount})`} v={formatINR(projectSpending.volumes)} />
                </div>
            </section>

            <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
                <section className="ps-card p-5 flex flex-col gap-3" aria-labelledby="proj-pods">
                    <SectionHeader
                        id="proj-pods"
                        title="Pods"
                        action={<Link to={`/pods?project=${projectId}`} className="ps-link">View all</Link>}
                    />
                    {containers.length > 0 ? (
                        <ul className="m-0 p-0 list-none flex flex-col">
                            {containers.slice(0, 5).map((container) => (
                                <li key={container.id} className="border-t border-ps-border first:border-t-0">
                                    <Link
                                        to={`/pods/${container.id}`}
                                        className="flex items-center justify-between gap-3 py-2.5 min-h-[44px] hover:text-ps-accent"
                                    >
                                        <span className="truncate text-ps-text">{container.name}</span>
                                        <StatusTag status={container.status} />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="m-0 py-6 text-center text-ps-muted text-sm">No pods yet</p>
                    )}
                </section>

                {isFileStorageEnabled() && (
                    <section className="ps-card p-5 flex flex-col gap-3" aria-labelledby="proj-storage">
                        <SectionHeader
                            id="proj-storage"
                            title="Storage"
                            action={<Link to={`/storage?project=${projectId}`} className="ps-link">View all</Link>}
                        />
                        {buckets.length + volumes.length > 0 ? (
                            <ul className="m-0 p-0 list-none flex flex-col">
                                {buckets.slice(0, 3).map((bucket) => (
                                    <li key={bucket.id} className="border-t border-ps-border first:border-t-0">
                                        <Link to={`/storage/${bucket.id}`} className="flex items-center justify-between gap-3 py-2.5 min-h-[44px] hover:text-ps-accent">
                                            <span className="truncate text-ps-text">{bucket.name}</span>
                                            <span className="ps-tag ps-tag-info">Bucket</span>
                                        </Link>
                                    </li>
                                ))}
                                {volumes.slice(0, 3).map((volume) => (
                                    <li key={volume.id} className="border-t border-ps-border first:border-t-0">
                                        <Link to={`/volumes/${volume.id}`} className="flex items-center justify-between gap-3 py-2.5 min-h-[44px] hover:text-ps-accent">
                                            <span className="truncate text-ps-text">{volume.name}</span>
                                            <span className="ps-tag ps-tag-off">NFS</span>
                                        </Link>
                                    </li>
                                ))}
                                {(buckets.length > 3 || volumes.length > 3) && (
                                    <li className="pt-2 text-sm text-ps-subtle text-center">
                                        + {Math.max(0, buckets.length - 3) + Math.max(0, volumes.length - 3)} more storage resources
                                    </li>
                                )}
                            </ul>
                        ) : (
                            <p className="m-0 py-6 text-center text-ps-muted text-sm">No storage resources</p>
                        )}
                    </section>
                )}
            </div>
        </Page>
    );
};

export default ProjectOverview;
